// Pure helpers: formatting, take grouping per layer, stale badges, polling decisions. No DOM, no fetch.
import type { Costs, Generation, Layer, Role, Selection, Shot, Take, TakesResponse, Tier } from "./types.ts";

export const LAYERS: readonly Layer[] = ["keyframe", "video", "dialogue", "sfx", "music"];
/** Higgsfield reference price per episode (USD), used when the API does not send one. */
export const HIGGSFIELD_REFERENCE_USD = 100;

// ─── numbers ─────────────────────────────────────────────────────────────────

/** Postgres numerics may arrive as strings. Returns null for null/undefined/NaN. */
export function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** A cost in display form: always two decimals ("$0.02"). */
export function usd2(v: unknown): string {
  const n = num(v);
  if (n === null) return "–";
  return `${n < 0 ? "-" : ""}$${Math.abs(n).toFixed(2)}`;
}

/** The price on a generate button: "≈ $0.03", or "< $0.01" under half a cent (never a "$0.00" that reads as free). */
export function priceLabel(v: unknown): string {
  const n = num(v);
  if (n === null) return "";
  if (n > 0 && n < 0.005) return "< $0.01";
  return `≈ ${usd2(n)}`;
}

/** "9:16" → 0.5625 (width ÷ height), the shape every still of a project is framed in. 16:10 when unreadable. */
export function aspectRatio(aspect: string | null | undefined): number {
  const m = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/.exec(aspect ?? "");
  const w = Number(m?.[1]);
  const h = Number(m?.[2]);
  return m && w > 0 && h > 0 ? w / h : 1.6;
}

const TYPE_LABEL: Record<string, string> = {
  microdrama: "Microdrama",
  feature: "Feature",
  series: "Series",
  short: "Short film",
};
export const PROJECT_TYPES = TYPE_LABEL;
/** A feature or a short is one film: no episodes in the studio, its one episode is "the film". */
export const isFilm = (type: string): boolean => type === "feature" || type === "short";
/** "microdrama" → "Microdrama"; an unknown type is capitalised as it is. */
export const projectTypeLabel = (type: string): string =>
  TYPE_LABEL[type] ?? type.charAt(0).toUpperCase() + type.slice(1);

/** Money as shown on screen: always two decimals ("$0.27"); a real but sub-cent amount reads "<$0.01". */
export function formatUsd(v: unknown): string {
  const n = num(v);
  if (n === null) return "–";
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  if (a > 0 && a < 0.005) return `${sign}<$0.01`;
  return `${sign}$${a.toFixed(2)}`;
}

/** What was spent, as shown in lists, inspectors and the top bar: nothing spent reads "–", never "$0.00". */
export function spentText(v: unknown): string {
  const n = num(v);
  return n === null || n === 0 ? "–" : formatUsd(n);
}

export function formatSeconds(v: unknown): string {
  const n = num(v);
  if (n === null) return "–";
  return `${Number.isInteger(n) ? n : n.toFixed(1)}s`;
}

export function formatPct(part: unknown, whole: unknown): string {
  const p = num(part);
  const w = num(whole);
  if (p === null || w === null || w === 0) return "–";
  return `${Math.round((p / w) * 100)}%`;
}

// ─── roles ───────────────────────────────────────────────────────────────────

const RANK: Record<Role, number> = { viewer: 0, reviewer: 1, maker: 2, director: 3, producer: 4, owner: 5 };
export const atLeast = (role: Role | undefined, min: Role): boolean => !!role && RANK[role] >= RANK[min];

// ─── generations ─────────────────────────────────────────────────────────────

export const isActive = (g: Pick<Generation, "status">): boolean =>
  g.status === "queued" || g.status === "running";
export const hasActive = (gens: readonly Pick<Generation, "status">[]): boolean => gens.some(isActive);

const time = (s: string | null | undefined): number => (s ? Date.parse(s) || 0 : 0);

/** Newest first: queued_at, then final before draft, then higher attempt. */
export function byRecency(a: Generation, b: Generation): number {
  return (
    time(b.queued_at) - time(a.queued_at) ||
    (a.tier === b.tier ? 0 : a.tier === "final" ? 1 : -1) ||
    b.attempt - a.attempt
  );
}

export function groupGenerationsByTake(gens: readonly Generation[]): Map<string, Generation[]> {
  const out = new Map<string, Generation[]>();
  for (const g of gens) {
    const list = out.get(g.take_id);
    if (list) list.push(g);
    else out.set(g.take_id, [g]);
  }
  for (const list of out.values()) list.sort(byRecency);
  return out;
}

/** The generation whose media a take shows: latest succeeded one, a final beating a draft. */
export function mediaGeneration(gens: readonly Generation[]): Generation | undefined {
  const done = gens.filter((g) => g.status === "succeeded" && g.asset_version_id);
  return done.find((g) => g.tier === "final") ?? done[0];
}

// ─── takes grouped per layer ─────────────────────────────────────────────────

export interface TakeView {
  take: Take;
  /** newest first */
  generations: Generation[];
  /** what the take is doing now: the active generation, else the newest one */
  current: Generation | undefined;
  active: Generation | undefined;
  /** tier of what is shown/accepted: final once a final succeeded, else draft */
  tier: Tier | undefined;
  assetVersionId: string | null;
  selected: boolean;
  badge: StaleBadge | null;
  canSelect: boolean;
  canPromote: boolean;
  canRetry: boolean;
  canCancel: boolean;
  canReroll: boolean;
  /** actual cost of everything spent on this take, else the estimate of what is queued */
  costUsd: number | null;
  costIsEstimate: boolean;
}

export interface LayerView {
  kind: Layer;
  takes: TakeView[];
  selectedTakeId: string | null;
  selectedAssetVersionId: string | null;
}

export function takeView(take: Take, gens: readonly Generation[], selected: Selection | undefined): TakeView {
  const generations = [...gens].sort(byRecency);
  const active = generations.find(isActive);
  const current = active ?? generations[0];
  const media = mediaGeneration(generations);
  const isSelected = !!selected && selected.take_id === take.id;

  const succeededDraft = generations.some((g) => g.tier === "draft" && g.status === "succeeded");
  const finalBusyOrDone = generations.some(
    (g) =>
      g.tier === "final" && (g.status === "queued" || g.status === "running" || g.status === "succeeded"),
  );

  let actual = 0;
  let hasActual = false;
  let estimate = 0;
  let hasEstimate = false;
  for (const g of generations) {
    const c = num(g.cost_amount);
    if (g.status === "succeeded" && c !== null) {
      actual += c;
      hasActual = true;
    } else if (isActive(g)) {
      const e = num(g.estimated_cost_amount);
      if (e !== null) {
        estimate += e;
        hasEstimate = true;
      }
    }
  }

  const settled = take.state === "ready" || take.state === "failed" || take.state === "cancelled";
  return {
    take,
    generations,
    current,
    active,
    tier: media?.tier,
    assetVersionId:
      media?.asset_version_id ??
      take.asset_version_id ??
      (isSelected ? (selected?.asset_version_id ?? null) : null),
    selected: isSelected,
    badge: staleBadge(take),
    canSelect: take.state === "ready" && !isSelected && !active,
    canPromote: succeededDraft && !finalBusyOrDone && !active,
    canRetry: !active && current?.status === "failed",
    canCancel: !!active,
    canReroll: settled && !active,
    costUsd: hasActual ? actual + (hasEstimate ? estimate : 0) : hasEstimate ? estimate : null,
    costIsEstimate: !hasActual && hasEstimate,
  };
}

/** Takes of one shot grouped per layer, newest take first, with the selected take flagged. */
export function groupTakesByLayer(
  res: Pick<TakesResponse, "items" | "current_selection"> | undefined,
  gens: readonly Generation[] | ReadonlyMap<string, Generation[]>,
): Record<Layer, LayerView> {
  const byTake: ReadonlyMap<string, Generation[]> = Array.isArray(gens)
    ? groupGenerationsByTake(gens)
    : (gens as ReadonlyMap<string, Generation[]>);
  const out = {} as Record<Layer, LayerView>;
  for (const kind of LAYERS) {
    const selected = res?.current_selection.find((s) => s.kind === kind && s.take_id);
    const takes = (res?.items ?? [])
      .filter((t) => t.kind === kind && t.state !== "archived")
      .sort((a, b) => time(b.created_at) - time(a.created_at))
      .map((t) => takeView(t, byTake.get(t.id) ?? [], selected));
    out[kind] = {
      kind,
      takes,
      selectedTakeId: selected?.take_id ?? null,
      selectedAssetVersionId: selected?.asset_version_id ?? null,
    };
  }
  return out;
}

// ─── staleness ───────────────────────────────────────────────────────────────

export interface StaleBadge {
  tone: "stale" | "upstream";
  label: string;
  /** human-readable causes, one per reason */
  reasons: string[];
}

const REASON_TEXT: Record<string, string> = {
  shot_spec: "shot changed",
  shot: "shot changed",
  scene: "scene changed",
  dialogue_line: "dialogue changed",
  element: "element changed",
  element_look: "look changed",
};

export function staleReasonText(r: string): string {
  return REASON_TEXT[r] ?? r.replaceAll("_", " ");
}

/** Badge for a take (or a stale selection): stale reasons and/or an upstream keyframe change. */
export function staleBadge(t: {
  stale_reasons: readonly string[];
  upstream_changed: boolean;
}): StaleBadge | null {
  const reasons = [...new Set(t.stale_reasons.map(staleReasonText))];
  if (t.upstream_changed) reasons.push("keyframe changed");
  if (reasons.length === 0) return null;
  return t.stale_reasons.length > 0
    ? { tone: "stale", label: "stale", reasons }
    : { tone: "upstream", label: "upstream changed", reasons };
}

// ─── costs panel ─────────────────────────────────────────────────────────────

export interface CostSummary {
  total: number;
  providers: { provider: string; usd: number; share: string }[];
  draft: number;
  final: number;
  maker: number;
  acceptedSeconds: number;
  perAcceptedSecond: number | null;
  reference: number;
  delta: number;
  /** negative = cheaper than the reference */
  deltaPct: string;
}

export function summarizeCosts(c: Costs): CostSummary {
  const total = num(c.total_usd) ?? 0;
  const reference = num(c.reference_usd) ?? HIGGSFIELD_REFERENCE_USD;
  const providers = Object.entries(c.by_provider ?? {})
    .map(([provider, v]) => ({ provider, usd: num(v) ?? 0 }))
    .sort((a, b) => b.usd - a.usd)
    .map((p) => ({ ...p, share: formatPct(p.usd, total) }));
  const delta = num(c.delta_usd) ?? total - reference;
  return {
    total,
    providers,
    draft: num(c.by_tier?.draft) ?? 0,
    final: num(c.by_tier?.final) ?? 0,
    maker: num(c.maker_usd) ?? 0,
    acceptedSeconds: num(c.accepted_seconds) ?? 0,
    perAcceptedSecond: num(c.usd_per_accepted_second),
    reference,
    delta,
    deltaPct: reference ? `${delta > 0 ? "+" : ""}${Math.round((delta / reference) * 100)}%` : "–",
  };
}

// ─── shot summaries ──────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** One line from the acting jsonb: verb, subtext, gesture (the keys the pipeline import writes). */
export function actingSummary(acting: Shot["acting"] | null | undefined): string {
  const a = acting ?? {};
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = str(a[k]);
      if (v) return v;
    }
    return null;
  };
  const parts = [
    pick("verb"),
    pick("subtext") && `subtext: ${pick("subtext")}`,
    pick("gesture", "psychological_gesture") && `gesture: ${pick("gesture", "psychological_gesture")}`,
  ].filter(Boolean);
  return parts.join(" · ");
}

/** Natural ordering for shot codes: S2 before S10. */
export const compareCodes = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

// ─── polling ─────────────────────────────────────────────────────────────────

/**
 * Which shots need their takes re-read after a poll: shots owning a generation that is new or changed
 * status. Returns "all" when a changed generation belongs to a take we do not know yet (and has no scene).
 */
export function shotsToRefresh(
  prev: readonly Generation[],
  next: readonly Generation[],
  takeToShot: ReadonlyMap<string, string>,
): Set<string> | "all" {
  const before = new Map(prev.map((g) => [g.id, g.status]));
  const dirty = new Set<string>();
  for (const g of next) {
    if (before.get(g.id) === g.status) continue;
    if (g.scene_id && !g.shot_id) return "all";
    const shot = g.shot_id ?? takeToShot.get(g.take_id);
    if (!shot) return "all";
    dirty.add(shot);
  }
  return dirty;
}

/** Run async tasks with bounded concurrency; results keep input order. */
export async function pool<T>(limit: number, tasks: readonly (() => Promise<T>)[]): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  const worker = async () => {
    while (next < tasks.length) {
      const i = next++;
      results[i] = await tasks[i]!();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  return results;
}

/** Like pool, but after the first failure no new task starts; rejects with that failure once the running tasks settle. For paid batches. */
export async function poolUntilFailure<T>(limit: number, tasks: readonly (() => Promise<T>)[]): Promise<T[]> {
  const results: T[] = new Array(tasks.length);
  let next = 0;
  let failure: { error: unknown } | undefined;
  const worker = async () => {
    while (!failure && next < tasks.length) {
      const i = next++;
      try {
        results[i] = await tasks[i]!();
      } catch (error) {
        failure ??= { error };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
  if (failure) throw failure.error;
  return results;
}

// ─── models and errors ───────────────────────────────────────────────────────

/** Which `kind` values of GET /models can serve a layer (the API may name them either way). */
const MODEL_KINDS: Record<Layer, readonly string[]> = {
  keyframe: ["keyframe", "image"],
  video: ["video"],
  dialogue: ["dialogue", "tts", "speech", "audio"],
  sfx: ["sfx", "sound", "audio"],
  music: ["music", "audio"],
};

export function modelsForLayer<M extends { kind: string }>(models: readonly M[], layer: Layer): M[] {
  const kinds = MODEL_KINDS[layer];
  return models.filter((m) => kinds.includes(m.kind));
}

/** `provider/model_key`, the value of the model picker. */
export const modelValue = (m: { provider: string; model_key: string }): string =>
  `${m.provider}/${m.model_key}`;

export function parseModelValue(v: string): { provider?: string; modelKey?: string } {
  const i = v.indexOf("/");
  return i < 0 ? {} : { provider: v.slice(0, i), modelKey: v.slice(i + 1) };
}

/** A short text for a generation's `error` jsonb (or any thrown value). */
export function describeError(e: unknown): string {
  if (e === null || e === undefined) return "";
  if (typeof e === "string") return e;
  if (typeof e === "object") {
    const o = e as { message?: unknown; code?: unknown };
    if (typeof o.message === "string")
      return typeof o.code === "string" ? `${o.code}: ${o.message}` : o.message;
  }
  try {
    return JSON.stringify(e).slice(0, 300);
  } catch {
    return String(e);
  }
}
